import { ptBR } from '@/i18n/pt-BR';
import type { FaixaRow } from '@/lib/precificacao_form';
import { Button } from '@/components/ui';

/** Campo de entrada da configuração comercial; o formulário reusa nos campos de vigência. */
export const PRECIFICACAO_INPUT =
  'w-full border border-border bg-surface px-2 py-1 text-body-sm text-fg focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50';

interface FaixaEditorRowProps {
  index: number;
  row: FaixaRow;
  /** Volume inicial derivado do fim da faixa anterior; `null` enquanto indeterminado. */
  inicio: number | null;
  ultima: boolean;
  removivel: boolean;
  onChange: (patch: Partial<FaixaRow>) => void;
  onRemove: () => void;
}

/** Uma linha de faixa do editor: volume inicial derivado, limite, valores e remoção. */
export function FaixaEditorRow({
  index,
  row,
  inicio,
  ultima,
  removivel,
  onChange,
  onRemove,
}: FaixaEditorRowProps) {
  const numero = index + 1;
  return (
    <li
      data-testid={`faixa-row-${index}`}
      className="grid grid-cols-2 items-end gap-sc-2 border border-border p-sc-2 sm:grid-cols-5"
    >
      <div className="text-body-sm text-fg-muted">
        <span className="font-medium text-fg">
          {ptBR.precificacao.faixaOrdem} {numero}
        </span>
        <span data-testid={`faixa-inicio-${index}`} className="block">
          {ptBR.precificacao.faixaVolumeInicio}:{' '}
          {inicio === null ? ptBR.precificacao.valorAusente : inicio}
        </span>
      </div>
      <label className="flex flex-col gap-1 text-caption text-fg-muted">
        <span>{ptBR.precificacao.faixaVolumeFim}</span>
        <input
          type="number"
          min="1"
          value={row.volumeFim}
          disabled={ultima}
          placeholder={ultima ? ptBR.precificacao.volumeAberto : ''}
          aria-label={`${ptBR.precificacao.faixaVolumeFim} ${numero}`}
          data-testid={`faixa-volume-fim-${index}`}
          onChange={(e) => onChange({ volumeFim: e.target.value })}
          className={PRECIFICACAO_INPUT}
        />
      </label>
      <label className="flex flex-col gap-1 text-caption text-fg-muted">
        <span>{ptBR.precificacao.faixaExame}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={row.exameReais}
          aria-label={`${ptBR.precificacao.faixaExame} ${numero}`}
          data-testid={`faixa-exame-${index}`}
          onChange={(e) => onChange({ exameReais: e.target.value })}
          className={PRECIFICACAO_INPUT}
        />
      </label>
      <label className="flex flex-col gap-1 text-caption text-fg-muted">
        <span>{ptBR.precificacao.faixaCertificacao}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={row.certificacaoReais}
          aria-label={`${ptBR.precificacao.faixaCertificacao} ${numero}`}
          data-testid={`faixa-certificacao-${index}`}
          onChange={(e) => onChange({ certificacaoReais: e.target.value })}
          className={PRECIFICACAO_INPUT}
        />
      </label>
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={onRemove}
        disabled={!removivel}
        aria-label={`${ptBR.precificacao.removeFaixa} ${numero}`}
        data-testid={`faixa-remove-${index}`}
      >
        {ptBR.precificacao.removeFaixa}
      </Button>
    </li>
  );
}
